import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import GetApp from '@material-ui/icons/GetApp';
import Close from '@material-ui/icons/Close';
import { FormattedMessage, useIntl } from 'react-intl';

const LIGHT_IMAGE_HEIGHT = '70vh';

const useStyles = makeStyles((theme) => ({
  contentDark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 !important',
    backgroundColor: '#000',
    position: 'relative',
    minHeight: 300,
  },
  contentLight: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 !important',
    backgroundColor: theme.palette.background.default,
    position: 'relative',
  },
  header: {
    position: 'relative',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    paddingRight: theme.spacing(4),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSubtitle: {
    color: theme.palette.text.secondary,
  },
  headerMeta: {
    color: theme.palette.text.secondary,
  },
  headerClose: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  lightImageWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    height: LIGHT_IMAGE_HEIGHT,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  imageDark: {
    maxWidth: '100%',
    maxHeight: '80vh !important',
    display: 'block',
  },
  imageLight: {
    maxWidth: '100%',
    maxHeight: '100% !important',
    display: 'block',
  },
  overlayTitle: {
    position: 'absolute',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 13,
    maxWidth: '70%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  counterDark: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 13,
  },
  counterLight: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginRight: theme.spacing(1),
  },
  navDark: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 8,
    color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.4)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.6)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  navDarkPrev: {
    left: 10,
  },
  navDarkNext: {
    right: 10,
  },
  actionsLight: {
    justifyContent: 'space-between',
  },
  actionsRightGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  unavailable: {
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

const Lightbox = ({
  images,
  initialIndex,
  onClose,
  onDelete,
  onDownload,
  readOnly,
  variant,
  subtitle,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const [index, setIndex] = useState(() => {
    if (!images.length) return 0;
    if (initialIndex < 0) return 0;
    if (initialIndex >= images.length) return images.length - 1;
    return initialIndex;
  });
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  const isLight = variant === 'light';

  const prev = useCallback(() => {
    if (!images.length) return;
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = useCallback(() => {
    if (!images.length) return;
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!images.length) {
      onClose();
      return;
    }
    if (index >= images.length) setIndex(images.length - 1);
  }, [images, index, onClose]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    },
    [prev, next, onClose],
  );

  const touchStartX = useRef(0);
  const onTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const onTouchEnd = useCallback(
    (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (dx < -50) next();
      else if (dx > 50) prev();
    },
    [next, prev],
  );

  const handleDelete = useCallback(() => {
    if (readOnly || !onDelete) return;
    onDelete(index);
  }, [readOnly, onDelete, index]);

  const handleDownload = useCallback(() => {
    if (!onDownload) return;
    onDownload(images[index]);
  }, [onDownload, images, index]);

  if (!images.length || !images[index]) return null;

  const current = images[index];
  const hasNav = images.length > 1;

  return (
    <Dialog
      open
      onClose={onClose}
      onKeyDown={onKeyDown}
      maxWidth="md"
      fullWidth
      fullScreen={isSmall}
    >
      <DialogContent
        className={isLight ? classes.contentLight : classes.contentDark}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {isLight && (
          <div className={classes.header}>
            <Typography variant="subtitle2" className={classes.headerTitle}>
              {current.title || `${index + 1}`}
            </Typography>
            {subtitle && (
              <Typography variant="body2" className={classes.headerSubtitle}>
                {subtitle}
              </Typography>
            )}
            {current.meta && (
              <Typography variant="caption" className={classes.headerMeta}>
                {current.meta}
              </Typography>
            )}
            <IconButton
              className={classes.headerClose}
              onClick={onClose}
              aria-label={intl.formatMessage({
                id: 'lightbox.close',
                defaultMessage: 'Close',
              })}
            >
              <Close />
            </IconButton>
          </div>
        )}
        <div className={isLight ? classes.lightImageWrap : undefined}>
          {brokenIds.has(current.id) ? (
            <Typography variant="body2" className={classes.unavailable}>
              <FormattedMessage
                id="lightbox.unavailable"
                defaultMessage="File is no longer available"
              />
            </Typography>
          ) : (
            <img
              src={current.url}
              alt={current.title || `${index + 1}`}
              className={isLight ? classes.imageLight : classes.imageDark}
              onError={() =>
                setBrokenIds((prevIds) => {
                  const nextSet = new Set(prevIds);
                  nextSet.add(current.id);
                  return nextSet;
                })
              }
            />
          )}
        </div>
        {!isLight && current.title && (
          <Typography variant="caption" className={classes.overlayTitle}>
            {current.title}
          </Typography>
        )}
        {!isLight && hasNav && (
          <>
            <IconButton
              className={`${classes.navDark} ${classes.navDarkPrev}`}
              onClick={prev}
              aria-label={intl.formatMessage({
                id: 'lightbox.previous',
                defaultMessage: 'Previous image',
              })}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              className={`${classes.navDark} ${classes.navDarkNext}`}
              onClick={next}
              aria-label={intl.formatMessage({
                id: 'lightbox.next',
                defaultMessage: 'Next image',
              })}
            >
              <ChevronRight />
            </IconButton>
            <span className={classes.counterDark}>
              {index + 1} / {images.length}
            </span>
          </>
        )}
      </DialogContent>
      <DialogActions className={isLight ? classes.actionsLight : undefined}>
        {isLight ? (
          <>
            {onDownload && (
              <Button startIcon={<GetApp />} onClick={handleDownload}>
                <FormattedMessage id="lightbox.download" defaultMessage="Download" />
              </Button>
            )}
            <div className={classes.actionsRightGroup}>
              {hasNav && (
                <>
                  <span className={classes.counterLight}>
                    {index + 1} / {images.length}
                  </span>
                  <IconButton
                    size="small"
                    onClick={prev}
                    aria-label={intl.formatMessage({
                      id: 'lightbox.previous',
                      defaultMessage: 'Previous image',
                    })}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={next}
                    aria-label={intl.formatMessage({
                      id: 'lightbox.next',
                      defaultMessage: 'Next image',
                    })}
                  >
                    <ChevronRight />
                  </IconButton>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {!readOnly && onDelete && (
              <Button color="secondary" startIcon={<DeleteOutline />} onClick={handleDelete}>
                <FormattedMessage id="lightbox.delete" defaultMessage="Delete" />
              </Button>
            )}
            <Button onClick={onClose}>
              <FormattedMessage id="lightbox.close" defaultMessage="Close" />
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

Lightbox.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
      meta: PropTypes.string,
    }),
  ).isRequired,
  initialIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  readOnly: PropTypes.bool,
  variant: PropTypes.oneOf(['dark', 'light']),
  subtitle: PropTypes.string,
};

Lightbox.defaultProps = {
  initialIndex: 0,
  readOnly: false,
  variant: 'dark',
};

export default Lightbox;
